import client from './client';

/**
 * Schedules API
 *
 * All endpoints map to /api/schedules on the Spring Boot backend.
 */

/**
 * Create a scheduled interview.
 * @param {{ hostEmail: string, hostName: string, candidateEmail: string,
 *            scheduledAt: string, durationMinutes?: number, notes?: string }} data
 */
export const createSchedule = (data) => {
  console.log('[schedules] createSchedule', data);
  return client.post('/api/schedules', data);
};

/**
 * List scheduled interviews.
 * @param {string|object} hostEmailOrParams  Host email string, or query params
 *        e.g. `{ hostEmail }`, `{ orgId }`, `{}` for platform-wide (SUPER_ADMIN).
 */
export const listSchedules = (hostEmailOrParams) => {
  const params =
    typeof hostEmailOrParams === 'string'
      ? { hostEmail: hostEmailOrParams }
      : { ...(hostEmailOrParams || {}) };
  console.log('[schedules] listSchedules', params);
  return client.get('/api/schedules', { params });
};

/**
 * List the signed-in candidate's upcoming interviews (Phase 5).
 * Resolves the user from the JWT - no body or params needed.
 */
export const listMyCandidateSchedules = () => {
  console.log('[schedules] listMyCandidateSchedules');
  return client.get('/api/schedules/mine');
};

/**
 * Cancel a scheduled interview by ID.
 * @param {string|number} id
 */
export const cancelSchedule = (id) => {
  console.log('[schedules] cancelSchedule', id);
  return client.post(`/api/schedules/${id}/cancel`);
};

/**
 * Link a freshly created session to an existing scheduled interview.
 * Backend transitions the schedule status from SCHEDULED to ACTIVE.
 *
 * @param {string} scheduleId  - schedule UUID
 * @param {string} sessionId   - newly created session UUID
 */
export const linkSession = (scheduleId, sessionId) => {
  console.log('[schedules] linkSession', { scheduleId, sessionId });
  return client.post(`/api/schedules/${scheduleId}/link`, null, {
    params: { sessionId },
  });
};

/**
 * Resolve a schedule invite token (used by the candidate join link).
 * @param {string} token  - the opaque token embedded in the invite email link
 */
export const resolveToken = (token) => {
  console.log('[schedules] resolveToken', token);
  return client.get('/api/schedules/resolve', { params: { token } });
};

// ---------------------------------------------------------------------------
// Pipeline / Kanban
// ---------------------------------------------------------------------------

/**
 * Fetch the authenticated host's full Kanban board.
 * Backend always returns one column per InterviewStage, even when empty.
 *
 * @returns {Promise<{ data: Array<{ stage: string, cards: Array<object> }> }>}
 */
export const getKanbanBoard = () => {
  console.log('[schedules] getKanbanBoard');
  return client.get('/api/schedules/kanban');
};

/**
 * Move a schedule card to a different pipeline stage.
 *
 * @param {string} id    - schedule UUID
 * @param {string} stage - one of APPLIED | SCREENING | TECHNICAL | HR | OFFER | HIRED | REJECTED
 */
export const updateStage = (id, stage) => {
  console.log('[schedules] updateStage', { id, stage });
  return client.patch(`/api/schedules/${id}/stage`, { stage });
};

// ---------------------------------------------------------------------------
// Phase 7 - bulk + export
// ---------------------------------------------------------------------------

/**
 * Cancel many schedules in one shot. Returns a per-id outcome map -
 * partial failures do NOT abort the batch.
 *
 * @param {string[]} ids - schedule UUIDs
 */
export const bulkCancelSchedules = (ids) => {
  console.log('[schedules] bulkCancelSchedules', ids?.length, 'ids');
  return client.post('/api/schedules/bulk/cancel', { ids });
};

/**
 * Download the host's full schedule list as a CSV blob. Returns the
 * raw axios response with `data: Blob` so the caller can wire up an
 * <a download> link or trigger a browser save.
 */
export const exportSchedulesCsv = () => {
  console.log('[schedules] exportSchedulesCsv');
  return client.get('/api/schedules/export.csv', { responseType: 'blob' });
};
